import classNames from "classnames";
import { useDictionaryTools, type DictionaryMode } from "contexts/DictionaryToolsContext";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import Button from "components/ui/Button";
import BookmarkIcon from "images/bookmark.svg?react";
import DefineIcon from "images/define.svg?react";
import TranslateIcon from "images/translate.svg?react";
import "./TextSelectionMenu.css";

// Selecting text anywhere offers the dictionary menu. Subtrees that mark
// themselves with this class opt out — editors, search inputs and anything else
// where selecting text means something other than looking it up.
export const NO_SELECTION_CLASS_NAME = "no-dictionary-selection";

// An estimate, and only ever compared against the room below the selection.
// Placing the menu above uses its own real height, so a wrong guess here costs
// a flip, not a misplaced menu.
const APPROX_MENU_HEIGHT = 100;

type Selection = {
  text: string;
  range: Range;
};

// Viewport coordinates for the fixed-position menu.
type Placement = {
  top: number;
  left: number;
  above: boolean;
};

export default function TextSelectionMenu() {
  const { t } = useTranslation();
  const location = useLocation();
  const { openSidebar } = useDictionaryTools();

  const [selected, setSelected] = useState<Selection | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // A mousedown anywhere but on the menu itself dismisses the current menu,
    // and only a gesture that goes on to move the selection may offer a new one.
    // A click that leaves the selection alone — the sidebar's close button, with
    // the looked-up text still highlighted behind it — would otherwise hand the
    // menu straight back on its mouseup.
    let selectionMoved = false;

    const onSelectionChange = () => {
      selectionMoved = true;
    };

    const onMouseDown = (event: MouseEvent) => {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) {
        return;
      }
      selectionMoved = false;
      setSelected(null);
    };

    const onMouseUp = (event: MouseEvent) => {
      if (!selectionMoved) {
        return;
      }
      selectionMoved = false;

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest("." + NO_SELECTION_CLASS_NAME)) {
        return;
      }

      const selection = document.getSelection();
      const text = selection?.toString().trim();
      if (!selection?.rangeCount || !text) {
        return;
      }

      // Snapshot the range: the live one mutates with every later selection.
      setSelected({ text, range: selection.getRangeAt(0).cloneRange() });
    };

    document.addEventListener("selectionchange", onSelectionChange);
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // placement is derived from selected and must never outlive it: measured
  // against a selection that has gone, it would flash the menu at the old
  // coordinates. Hence clearing it on the way in here, and rendering only once
  // both are set.
  useEffect(() => {
    if (!selected) {
      setPlacement(null);
      return;
    }

    const place = () => setPlacement(getPlacement(selected));
    place();

    // Scrolling any ancestor or resizing the window moves the selection, so the
    // fixed-position menu has to re-measure to stay attached to it.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [selected]);

  useEffect(() => {
    setSelected(null);
  }, [location.pathname]);

  if (!selected || !placement) {
    return null;
  }

  const handleAction = (mode: DictionaryMode) => () => {
    openSidebar(mode, selected.text);
    setSelected(null);
  };

  const isPhrase = selected.text.split(" ").length > 1;

  const style = {
    "--selection-menu-top": `${placement.top}px`,
    "--selection-menu-left": `${placement.left}px`,
  } as CSSProperties;

  return createPortal(
    <div
      ref={menuRef}
      className={classNames("selection-context-menu", { above: placement.above })}
      style={style}
      // Keep the page's text selection highlighted while interacting with the menu.
      onMouseDown={event => event.preventDefault()}
    >
      <Button
        variant="plain"
        className="action-with-image"
        onClick={handleAction("define")}
      >
        <DefineIcon className="action-icon" />
        <span>{t("Common.actions.define")}</span>
      </Button>
      <span className="vertical-separator"></span>
      <Button
        variant="plain"
        className="action-with-image"
        onClick={handleAction("translate")}
      >
        <TranslateIcon className="action-icon" />
        <span>{t("Common.actions.translate")}</span>
      </Button>
      <span className="vertical-separator"></span>
      <Button
        variant="plain"
        className="action-with-image"
        onClick={handleAction("save")}
      >
        <BookmarkIcon className="action-icon" />
        <span>
          {t(isPhrase ? "Common.actions.savePhrase" : "Common.actions.saveWord")}
        </span>
      </Button>
    </div>,
    // Portaled so the menu's fixed positioning can't be captured by a
    // transformed ancestor such as the closed drawer.
    document.body
  );
}

function getPlacement(selected: Selection): Placement | null {
  const rect = selected.range.getBoundingClientRect();
  // A range whose nodes were re-rendered away measures as an empty rect.
  if (rect.width === 0 && rect.height === 0) {
    return null;
  }

  const above = rect.bottom + APPROX_MENU_HEIGHT > window.innerHeight;

  return {
    top: above ? rect.top : rect.bottom,
    left: rect.left,
    above,
  };
}
